import { useFeatureFlag } from '#components/feature-flag/use-feature-flag.js';
import { useShowBillingFlow } from '#components/feature-flag/use-show-billing-flow.js';
import { canCustomerTriggerAnalysis } from '#src/routes/results/analysis-results-page/components/analyze-trigger-visibility.js';
import { getRevisionActionKind, RevisionActionKind } from './revision-timeline-utils.js';

import type { ActionableRevision } from './revision-timeline-utils.js';

/** A revision's next action, after any feature gating has been applied. */
export interface RevisionAction {
	kind: RevisionActionKind;
	label: string;
}

const ACTION_LABELS: Record<RevisionActionKind, string> = {
	[RevisionActionKind.Upload]: 'Upload files',
	[RevisionActionKind.BomReview]: 'Review BOM',
	[RevisionActionKind.ViewProgress]: 'View progress',
	[RevisionActionKind.StartAnalysis]: 'Start analysis',
	[RevisionActionKind.AddCredit]: 'Update plan',
	[RevisionActionKind.ContactSales]: 'Contact Cadstrom',
	[RevisionActionKind.ViewResults]: 'View results',
	[RevisionActionKind.ViewError]: 'View error',
};

/**
 * The kinds that ask something of the customer, as opposed to the ones that
 * only open a revision to look at.
 */
const CALLS_TO_ACTION: ReadonlySet<RevisionActionKind> = new Set([
	RevisionActionKind.Upload,
	RevisionActionKind.BomReview,
	RevisionActionKind.StartAnalysis,
	RevisionActionKind.AddCredit,
	RevisionActionKind.ContactSales,
]);

/**
 * Whether an action is the customer's move rather than somewhere to look.
 *
 * @param kind Action kind to test.
 * @returns True when the control asks the customer to do something.
 */
export function isCallToAction(kind: RevisionActionKind): boolean {
	return CALLS_TO_ACTION.has(kind);
}

/**
 * A revision's single next action: where it should send the user and what the
 * control reads.
 *
 * Two kinds are demoted to a plain progress view when the work they name is not
 * the customer's to do: BOM review without the `bom-resolution` flag, and the
 * analysis gate for an organization whose runs Cadstrom dispatches. Adding credit
 * is the customer's own move only for a self-serve organization; any other is
 * sent to sales instead.
 *
 * @param revision Revision to resolve, or null when there is none.
 * @param orgName Organization the revision belongs to, which decides whether
 *   the analysis gate is the customer's to clear.
 * @returns The action, or null when the revision offers none and while the flag
 *   gating BOM review is still unresolved.
 */
export function useRevisionAction(revision: ActionableRevision | null, orgName: string): RevisionAction | null {
	const isBomResolutionEnabled = useFeatureFlag('bom-resolution');
	const showBillingFlow = useShowBillingFlow(orgName);

	if (revision === null) {
		return null;
	}

	const kind = getRevisionActionKind(revision);
	if (kind === null) {
		return null;
	}

	switch (kind) {
		case RevisionActionKind.BomReview: {
			// Undefined means PostHog has not answered yet, so there is no action to
			// resolve until it does.
			if (isBomResolutionEnabled === undefined) {
				return null;
			}
			return toAction(isBomResolutionEnabled ? RevisionActionKind.BomReview : RevisionActionKind.ViewProgress);
		}
		case RevisionActionKind.StartAnalysis: {
			// The same rule the results page offers its Analyze button by, so the two
			// cannot promise different things about the same revision.
			const canAnalyze = canCustomerTriggerAnalysis({
				revisionStatus: revision.status,
				revisionAutomationMode: revision.automationMode,
				showBillingFlow,
				isAnalyzing: false,
			});
			return toAction(canAnalyze ? RevisionActionKind.StartAnalysis : RevisionActionKind.ViewProgress);
		}
		case RevisionActionKind.AddCredit: {
			return toAction(showBillingFlow ? RevisionActionKind.AddCredit : RevisionActionKind.ContactSales);
		}
		case RevisionActionKind.Upload:
		case RevisionActionKind.ContactSales:
		case RevisionActionKind.ViewProgress:
		case RevisionActionKind.ViewResults:
		case RevisionActionKind.ViewError: {
			return toAction(kind);
		}
	}
}

/**
 * Pair an action kind with its label.
 *
 * @param kind Action kind to label.
 * @returns The action.
 */
function toAction(kind: RevisionActionKind): RevisionAction {
	return { kind, label: ACTION_LABELS[kind] };
}
